import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { DataTable, FilterDialog } from "../../components";
import { deleteCoach, getCoachList, getCoachNationalities } from "../../api/coaches";
import { ICoach } from "../../models/ICoach";
import { exportDataToExcel } from "../../utils/excel";
import { BrandGoogleSolid, CheckOctagon, CloudDownload, DangerOctagon, Envelope } from "@mynaui/icons-react";
import { Filter, FilterButton } from "../../components/FilterDialog";
import moment from "moment";
import { getLanguageList } from "../../api/general";
import { useNavigate, useSearchParams } from "react-router-dom";
import { assignRole } from "../../api/auth";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { useAppDispatch } from "../../store/store";
import "./styles.scss";
import { useDebouncedSearch } from "../../hooks";
import { Button, Loader, PageHeader } from "../../ui";

const Representers = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [representers, setRepresenters] = useState<ICoach[]>([]);

  const [exportLoading, setExportLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [sortColumn, setSortColumn] = useState<"firstName" | "createdAt" | "nationality" | "gender">("createdAt");

  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState<Filter[]>([]);

  // Ref to track current fetch request and prevent duplicates
  const currentFetchRef = useRef<string>("");
  const isFetchingRef = useRef(false);

  const parseFiltersFromParams = useCallback(() => {
    const params = Object.fromEntries(searchParams.entries());
    const gender = params.gender?.split(",") || [];
    const nationality = params.nationality?.split(",") || [];
    const languages = params.languages?.split(",") || [];
    const verified = params.verified || "";

    return { gender, nationality, languages, verified };
  }, [searchParams]);

  const getLanguages = async () => {
    try {
      const res = await getLanguageList();
      return res.data;
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const getNationalities = async () => {
    try {
      const res = await getCoachNationalities();
      return res.data;
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const handleSortChange = useCallback(
    (columnKey: "firstName" | "createdAt" | "nationality" | "gender", direction: "asc" | "desc") => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("sortColumn", columnKey);
        newParams.set("sortDirection", direction);
        newParams.delete("page");
        return newParams;
      });
    },
    [setSearchParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        if (newPage > 1) {
          newParams.set("page", newPage.toString());
        } else {
          newParams.delete("page");
        }

        return newParams;
      });
    },
    [setSearchParams]
  );

  const handleRoleSwitch = async (coach: ICoach) => {
    setLoading(true);
    try {
      await assignRole({ email: coach.email, role: "player" });
      // Trigger refetch by updating a URL param temporarily
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("_refetch", Date.now().toString());
        return newParams;
      });
    } catch (error) {
      console.error("error switching role");
    } finally {
      dispatch(
        setGeneralAlertMessage({
          message: `${coach.firstName} ${coach.lastName ?? ""} is no longer a representer`,
          type: "success",
        })
      );
    }
  };

  const handleViewCoach = (coach: ICoach) => {
    navigate(`${coach.id}`);
  };

  const handleSearch = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const prevSearch = prev.get("search") || "";

        // Only update if search value actually changed
        if (prevSearch === value) {
          return prev;
        }

        const newParams = new URLSearchParams(prev);
        if (value) {
          newParams.set("search", value);
        } else {
          newParams.delete("search");
        }
        newParams.delete("page"); // Reset to first page when searching
        return newParams;
      });
    },
    [setSearchParams]
  );

  const { searchValue, setSearchValue } = useDebouncedSearch(handleSearch, 500, searchParams.get("search") || "");

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const { gender, languages, nationality, verified } = parseFiltersFromParams();
      const res = await getCoachList(1, 1000, "", sortColumn, sortDirection, gender, nationality, languages, verified);
      exportDataToExcel(
        res.data.items.map((player) => ({
          name: `${player.firstName} ${player.lastName ?? ""}`,
          email: player.email,
          ["Account Type"]: player.accountType,
          gender: player.gender,
          ["Email Verified"]: player.emailVerified,
          nationality: player.nationality,
          birthDate: player.birthDate,
          phoneNumber: player.phoneNumber,
          languages: player.languages.map((x) => x.name).join(", "),
          ["Date Registered"]: player.createdAt,
        })),
        "players"
      );
    } catch (error: any) {
      console.error(error.message);
    } finally {
      setExportLoading(false);
    }
  };

  const handleFilterApply = (filters: Filter[]) => {
    setFilters(filters);

    const filtersApplied = filters.filter((x) => x.options?.some((o) => o.selected));

    let genderFilters: string[] = [];
    let nationalityFilters: string[] = [];
    let languageFilters: string[] = [];
    let verifiedFilter: string = "";

    filtersApplied.forEach((filter) => {
      const value = filter.options ? filter.options.filter((x) => x.selected).map((x) => x.label) : [];

      if (filter.label === "Gender") genderFilters = value;
      if (filter.label === "Nationality") nationalityFilters = value;
      if (filter.label === "Languages") languageFilters = value;
      if (filter.label === "Email Verified") {
        const value = filter.options ? filter.options.find((x) => x.selected)?.label : "";
        if (value === "Verified") verifiedFilter = "true";
        if (value === "Unverified") verifiedFilter = "false";
      }
    });

    const newParams = new URLSearchParams();

    if (genderFilters.length > 0) newParams.set("gender", genderFilters.join(","));
    if (nationalityFilters.length > 0) newParams.set("nationality", nationalityFilters.join(","));
    if (languageFilters.length > 0) newParams.set("languages", languageFilters.join(","));
    if (verifiedFilter) newParams.set("verified", verifiedFilter);
    setSearchParams(newParams);
  };

  const handleDelete = async (item: ICoach) => {
    setLoading(true);
    try {
      await deleteCoach(item.id);
      // Trigger refetch by updating a URL param temporarily
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("_refetch", Date.now().toString());
        return newParams;
      });
    } catch (error: any) {
      console.error("coach deletion failed", error.message);
    }
  };

  const getData = async () => {
    const languages = await getLanguages();
    const nationalities = await getNationalities();
    const { gender, nationality, languages: langParams, verified } = parseFiltersFromParams();

    const newFilters: Filter[] = [
      {
        label: "Gender",
        options: [
          { label: "Male", value: "Male", selected: gender.includes("Male") },
          { label: "Female", value: "Female", selected: gender.includes("Female") },
        ],
      },
      {
        label: "Nationality",
        options: nationalities?.map((item: string) => ({
          label: item,
          value: item,
          selected: nationality.includes(item),
        })),
      },
      {
        label: "Email Verified",
        radio: true,
        options: [
          { label: "Verified", value: "true", selected: verified === "true" },
          { label: "Unverified", value: "false", selected: verified === "false" },
        ],
      },
      {
        label: "Languages",
        showMore: false,
        options: languages.map((item: { name: string; id: string }) => ({
          label: item.name,
          value: item.id,
          selected: langParams.includes(item.id),
        })),
      },
    ];

    setFilters(newFilters);
  };

  useEffect(() => {
    document.title = "Representers";
    getData();
  }, []);

  // SINGLE useEffect - handles ALL data fetching
  useEffect(() => {
    const fetchData = async () => {
      // Create a unique key for this fetch request
      const fetchKey = searchParams.toString();

      // If we're already fetching this exact request, skip
      if (isFetchingRef.current && currentFetchRef.current === fetchKey) {
        return;
      }

      // Mark as fetching
      isFetchingRef.current = true;
      currentFetchRef.current = fetchKey;

      // Parse params
      const page = searchParams.get("page");
      const sortCol = searchParams.get("sortColumn");
      const sortDir = searchParams.get("sortDirection");
      const search = searchParams.get("search") || "";
      const gender = searchParams.get("gender") ? searchParams.get("gender")!.split(",") : [];
      const nationality = searchParams.get("nationality") ? searchParams.get("nationality")!.split(",") : [];
      const languages = searchParams.get("languages") ? searchParams.get("languages")!.split(",") : [];
      const verified = searchParams.get("verified") || "";

      const pageNum = page ? parseInt(page) : 1;
      const sortColumn =
        sortCol && ["firstName", "createdAt", "nationality", "gender"].includes(sortCol)
          ? (sortCol as any)
          : "createdAt";
      const sortDirection = sortDir && ["asc", "desc"].includes(sortDir) ? (sortDir as any) : "desc";

      // Update local state
      setPageNumber(pageNum);
      setSortColumn(sortColumn);
      setSortDirection(sortDirection);
      setLoading(true);

      try {
        const { data } = await getCoachList(
          pageNum,
          pageSize,
          search,
          sortColumn,
          sortDirection,
          gender,
          nationality,
          languages,
          verified
        );

        // Only update if this is still the current request
        if (currentFetchRef.current === fetchKey) {
          setRepresenters(data.items);
          setTotalPages(data.pagination.totalPages);
          setTotalItems(data.pagination.total);
        }
      } catch (err: any) {
        if (err.message === "No coaches found") setRepresenters([]);
        console.error("Error fetching representers", err);
      } finally {
        // Only set loading false if this is still the current request
        if (currentFetchRef.current === fetchKey) {
          setLoading(false);
          isFetchingRef.current = false;
        }
      }
    };

    fetchData();
  }, [searchParams, pageSize]);

  const filtersApplied = useMemo(() => filters.filter((x) => x.options?.some((o) => o.selected)).length, [filters]);

  return (
    <div className="coaches-page">
      <PageHeader
        title="Representers"
        count={representers.length}
        actions={
          <>
            <FilterButton count={filtersApplied} onClick={() => setIsFilterDialogOpen(true)} />
            <Button
              icon={<CloudDownload size={18} />}
              loading={exportLoading}
              onClick={handleExport}
            >
              Export
            </Button>
          </>
        }
      />

      <FilterDialog
        isOpen={isFilterDialogOpen}
        onApply={handleFilterApply}
        onClose={() => setIsFilterDialogOpen(false)}
        filters={filters}
      />

      {loading && representers.length === 0 ? (
        <Loader />
      ) : (
        <DataTable
          id={"representers-table"}
          data={representers}
          totalItems={totalItems}
          editable
          canDelete
          onDelete={handleDelete}
          label="representers"
          pageNumber={pageNumber}
          pageSize={pageSize}
          totalPages={totalPages}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onViewItem={handleViewCoach}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
          onSwitchRole={handleRoleSwitch}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          loading={loading}
          columns={[
            {
              key: "firstName",
              label: "Name",
              render: (_: any, row: ICoach) => `${row.firstName} ${row.lastName ?? ""}`,
              sortable: true,
            },
            {
              key: "accountType",
              label: "",
              render: (_: any, row: ICoach) =>
                row.accountType === "email" ? (
                  <Envelope className="people-account-icon" size={16} />
                ) : (
                  <BrandGoogleSolid className="people-account-icon" size={16} />
                ),
            },
            { key: "email", label: "Email" },
            { key: "gender", label: "Gender", sortable: true },
            {
              key: "emailVerified",
              label: "Verified",
              center: true,
              render: (_: any, row: ICoach) =>
                row.emailVerified ? (
                  <CheckOctagon size={20} className="people-verified" />
                ) : (
                  <DangerOctagon size={20} className="people-unverified" />
                ),
            },
            { key: "nationality", label: "Nationality", sortable: true },
            {
              key: "birthDate",
              label: "Birthdate",
              uppercase: true,
              render: (_: any, row: ICoach) => (row.birthDate ? moment(row.birthDate).format("DD MMM YYYY") : "-"),
            },
            {
              key: "languages",
              label: "Languages",
              render: (_: any, row: ICoach) =>
                row.languages.length > 0 ? row.languages.map((x) => x.name).join(", ") : "-",
            },
            {
              key: "createdAt",
              label: "Created At",
              render: (_: any, row: ICoach) => moment(row.createdAt).format("DD MMM, YYYY"),
              sortable: true,
            },
          ]}
        />
      )}
    </div>
  );
};

export default Representers;
